import React, { useEffect, useState } from "react";
import { TextInput, Loader } from "@mantine/core";
import type { FieldComponentProps } from "@/pages/ClientProfile/types";
import { useProfileStore } from "@/pages/ClientProfile/store/profileStore";
import { useParams } from "react-router-dom";
import {
  formatPhoneNumber,
  formatSSNNumber,
} from "@/pages/ClientProfile/utils";
import { showFieldError } from "@utils/reviewFieldUtils.ts";

interface TextFieldProps extends FieldComponentProps {
  fieldType?: "text" | "email" | "date";
  mutationType?: "profile" | "ocr";
  formId?: string;
}

const TextField: React.FC<TextFieldProps> = ({
  commonProps,
  field,
  fieldType = "text",
  mutationType: _mutationType = "profile",
  formId: _formId,
}) => {
  const { userId } = useParams();
  const updateProfileField = useProfileStore(
    (state) => state.updateProfileField
  );

  const shouldFormatPhone = field.name.includes("phone");
  const shouldFormatSSN = field.name.includes("ssn");

  const [loadingState, setLoadingState] = useState(false);
  const [optimisticValue, setOptimisticValue] = useState(field.value ?? "");

  useEffect(() => {
    setOptimisticValue(field.value ?? "");
  }, [field.value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    let newValue = e.target.value;

    if (shouldFormatPhone) {
      newValue = formatPhoneNumber(newValue);
    } else if (shouldFormatSSN) {
      newValue = formatSSNNumber(newValue);
    }

    setOptimisticValue(newValue);
    field.onChange(newValue);
  };

  // Refactored: Always intercept save for email fields
  const doSave = async (value: string) => {
    setLoadingState(true);
    try {
      await updateProfileField(userId ?? "", field.name, value);
      // Success notification is shown by the store method
      setLoadingState(false);
    } catch (error: any) {
      showFieldError(error, field.name);
      setOptimisticValue(field.value ?? "");
      setLoadingState(false);
    }
    field.onBlur();
  };

  const handleBlur = (): void => {
    doSave(optimisticValue);
  };

  return (
    <>
      <TextInput
        {...commonProps}
        {...field}
        value={optimisticValue}
        onChange={handleChange}
        onBlur={handleBlur}
        type={fieldType}
        variant="filled"
        disabled={loadingState}
        rightSection={
          loadingState || commonProps.rightSection ? <Loader size="xs" /> : null
        }
      />
    </>
  );
};

export default TextField;
